import { registerService } from './lib';
import type { WealthOrder } from './wealth';

export type SpotGridBotStatus =
  | 'CREATED'
  | 'STARTING'
  | 'RUNNING'
  | 'PAUSED'
  | 'STOPPING'
  | 'STOPPED'
  | 'FINISHED'
  | 'ERROR';

export type SpotGridBotGridType = 'ARITHMETIC' | 'GEOMETRIC';

export type SpotGridBotLevel = {
  id: string;
  level_index: number;
  price: string;
  buy_price: string;
  sell_price: string;
  quantity_per_grid: string;
  has_active_buy_order: boolean;
  has_active_sell_order: boolean;
};

export type SpotGridBotBot = {
  id: string;
  user_id: string;
  pair: string;
  base_coin: string;
  quote_coin: string;
  grid_type: SpotGridBotGridType;
  lower_price: string;
  upper_price: string;
  stop_loss_price: string | null;
  take_profit_price: string | null;
  grid_count: number;
  initial_quote_amount: string;
  initial_base_amount: string;
  current_quote_balance: string;
  current_base_balance: string;
  status: SpotGridBotStatus;
  last_known_price: string | null;
  total_profit: string;
  total_fees: string;
  total_trades: number;
  pause_reason: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  stopped_at: string | null;
  net_profit: string;
  net_profit_percentage: string;
  completed_cycles: number;
  running_duration_ms: number | null;
};

export type SpotGridBotOrder = {
  id: string;
  level_index: number;
  created_at: Date;
  paired_order_id: string | null;
  is_cycle_complete: boolean;
  order: WealthOrder | null;
};

export type SpotGridBotCreateParams = {
  pair: string;
  grid_type: SpotGridBotGridType;
  lower_price: string;
  upper_price: string;
  grid_count: number;
  initial_quote_amount: string;
  initial_base_amount?: string;
  stop_loss_price: string | null;
  take_profit_price: string | null;
};

export type SpotGridBotUpdateParams = {
  grid_type?: SpotGridBotGridType;
  lower_price?: string;
  upper_price?: string;
  grid_count?: number;
  initial_quote_amount?: string;
  initial_base_amount?: string;
  stop_loss_price?: string | null;
  take_profit_price?: string | null;
};

declare module './lib' {
  interface ServiceRegistry {
    'spot-grid-bot': ServiceEndpoints<{
      'get:bots/': {
        params: {
          pair?: string;
          status?: string;
        };
        response: SpotGridBotBot[];
      };
      'get:bots/{slug}': {
        params: {
          slug: string;
        };
        response: SpotGridBotBot;
      };
      'post:bots': {
        params: SpotGridBotCreateParams;
        response: SpotGridBotBot;
      };
      'put:bots/{slug}': {
        params: SpotGridBotUpdateParams & { slug: string };
        response: SpotGridBotBot;
      };
      'delete:bots/{slug}': {
        params: {
          slug: string;
        };
      };
      'post:bots/{slug}/start': {
        params: {
          slug: string;
        };
        response: SpotGridBotBot;
      };
      'post:bots/{slug}/stop': {
        params: {
          slug: string;
        };
        response: SpotGridBotBot;
      };
      'post:bots/{slug}/pause': {
        params: {
          slug: string;
        };
        response: SpotGridBotBot;
      };
      'post:bots/{slug}/resume': {
        params: {
          slug: string;
        };
        response: SpotGridBotBot;
      };
      'post:bots/{slug}/finish': {
        params: {
          slug: string;
        };
        response: SpotGridBotBot;
      };
      'get:bots/{slug}/orders': {
        params: {
          slug: string;
        };
        response: SpotGridBotOrder[];
      };
      'get:bots/{slug}/levels': {
        params: {
          slug: string;
        };
        response: SpotGridBotLevel[];
      };
    }>;
  }
}

registerService('spot-grid-bot', '/proxy/spot-grid-bot/v1');
